import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { hasRemainingCapacity, isBookableSessionRow } from 'utils/session';
import { CancellationPolicy } from 'types/listings';
import { IPriceTier } from 'types/experience.types';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';
import { currencyFormat } from 'utils';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import type { ITax } from 'types/experience.types';
import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  SessionStatus,
  SessionStatusFilter,
  AvailabilityTimeFrame,
  isContinuousScheduleType,
  CollectionFirstAvailableDateResponse as FirstAvailableDate,
  ResourceGroupV3,
} from '@kouto/types';
import { getProductFromResourceGroups } from 'features/analytics/services/analytics.helpers';
import { analyticsManager } from 'features/analytics';
import toDateString from 'utils/date/toDateString';
import getEndOfMonth from 'utils/date/getEndOfMonth';
import useSessions from 'api/session/useSessions';
import { MAX_SESSIONS_PER_DAY_FOR_AVAILABILITY } from 'api/session/constants';
import fromMoment from 'utils/date/fromMoment';
import { format } from 'date-fns';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import fromDateAndTimeString from 'utils/date/fromDateAndTimeString';
import { Brand } from 'api/brand/fetchBrandById';
import useGetEligibleProgram from 'api/brand/useGetEligibleProgram';
import { PublicListingDetailsV3 } from 'types/listingV3';
import fromDateString from 'utils/date/fromDateString';
import getStartOfMonth from 'utils/date/getStartOfMonth';
import useContinuousStartingPrice from 'features/Reserve/hooks/useContinuousStartingPrice';
import { NonMappedResourceGroupCard } from './NonMappedResourceGroupCard';

type PriceTier = IPriceTier & {
  id: string;
};

interface NonMappedResourceGroupCardWrapperProps {
  cancellationPolicy?: CancellationPolicy;
  listing: PublicListingDetailsV3;
  group: ResourceGroupV3;
  isLoading: boolean;
  selectedDate?: Date;
  vibes?: string[];
  promoterBrand?: Brand;
  firstAvailableDate?: FirstAvailableDate;
  isLoadingFirstAvailableDate: boolean;
}

export const NonMappedResourceGroupCardWrapper = ({
  cancellationPolicy,
  listing,
  group,
  isLoading,
  selectedDate: pageSelectedDate,
  vibes,
  promoterBrand,
  firstAvailableDate,
  isLoadingFirstAvailableDate,
}: NonMappedResourceGroupCardWrapperProps) => {
  const { t: translate } = useTranslation();
  const currency = useBrandCurrency();
  const { locale } = useGlobalDateLocale();
  const { formattedNumber } = useCurrencyFormatter();
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();
  const { data: eligibleProgram } = useGetEligibleProgram(
    listing?.brandId,
    listing?.id && listing?.brandId && listing?.productLine
      ? {
          listings: [
            {
              id: listing.id,
              brandId: listing.brandId,
              productLine: listing.productLine,
            },
          ],
        }
      : null,
  );

  // First available date for the resource group. This will be undefined if there is no future availability
  const resourceGroupFirstAvailableDate = useMemo(() => {
    return firstAvailableDate?.resourceGroups.find((g) => g.id === group.id)
      ?.firstAvailableDate;
  }, [firstAvailableDate, group.id]);

  const firstAvailSession = resourceGroupFirstAvailableDate?.sessions?.[0];

  const [localSelectedDate, setLocalSelectedDate] = useState<Date | undefined>(
    pageSelectedDate,
  );
  const [hasManuallySelectedLocalDate, setHasManuallySelectedLocalDate] =
    useState(false);
  const hasManuallySelectedDate =
    !!pageSelectedDate || hasManuallySelectedLocalDate;

  // Card date picker variables
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const hasDatePickerOpened = useRef(false);
  const [datePickerViewedMonth, setDatePickerViewedMonth] = useState<
    Date | undefined
  >(localSelectedDate);

  const isViewingDifferentMonth =
    datePickerViewedMonth &&
    localSelectedDate &&
    (datePickerViewedMonth.getMonth() !== localSelectedDate.getMonth() ||
      datePickerViewedMonth.getFullYear() !== localSelectedDate.getFullYear());

  useEffect(() => {
    if (datePickerOpen && !hasDatePickerOpened.current) {
      hasDatePickerOpened.current = true;
    }
  }, [datePickerOpen]);

  // Keep pageSelectedDate in sync with localSelectedDate
  useEffect(() => {
    setLocalSelectedDate(pageSelectedDate);
  }, [pageSelectedDate]);

  // Keep datePickerViewedMonth in sync when the picker is closed
  useEffect(() => {
    if (!datePickerOpen) {
      setDatePickerViewedMonth(localSelectedDate);
    }
  }, [localSelectedDate, datePickerOpen]);

  useEffect(() => {
    // If a date is already selected, do nothing
    if (localSelectedDate) return;

    const hasFirstDate = firstAvailableDate;
    const groupScheduledDate = resourceGroupFirstAvailableDate?.scheduledDate;

    // Case 1: There IS a collection-level firstAvailableDate but the group has none → default to today
    if (hasFirstDate && !groupScheduledDate) {
      setLocalSelectedDate(new Date());
      return;
    }

    // Case 2: Use the resource group's first available date
    if (groupScheduledDate) {
      setLocalSelectedDate(fromDateString(groupScheduledDate));
    }
  }, [
    resourceGroupFirstAvailableDate?.scheduledDate,
    localSelectedDate,
    firstAvailableDate,
    group.id,
  ]);

  const { sessions: hookSessions, isLoading: isSessionsLoading } = useSessions({
    listingId: listing.resourceGroupCollectionId,
    from: getStartOfMonth(datePickerViewedMonth ?? localSelectedDate),
    resourceGroupId: group.id,
    skip:
      !listing.resourceGroupCollectionId ||
      isLoadingFirstAvailableDate ||
      !localSelectedDate,
    to: getEndOfMonth(datePickerViewedMonth ?? localSelectedDate),
    maxSessionsPerDay: MAX_SESSIONS_PER_DAY_FOR_AVAILABILITY,
    // Server-side availability: without it, a day whose earliest sessions are all
    // booked/blocked fills the cap with dead rows and reads as fully unavailable.
    status: SessionStatusFilter.AVAILABLE,
  });

  const trackAnalyticsEvent = () => {
    if (listing) {
      analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
        [ANALYTICS_PROPERTY.ExperienceId]: group?.id,
        [ANALYTICS_PROPERTY.ExperienceTitle]:
          group?.title ?? 'Title unavailable',
        [ANALYTICS_PROPERTY.ExperienceCategory]: listing.category?.name,
        [ANALYTICS_PROPERTY.ListId]: listing.id,
        [ANALYTICS_PROPERTY.ListName]: listing.title ?? 'Title unavailable',
        [ANALYTICS_PROPERTY.Products]: JSON.stringify(
          getProductFromResourceGroups({
            groups: [group],
            categoryName: listing.category?.name,
            session: hookSessions[0],
          }),
        ),
      });
    }
  };

  const apiSelectedSession =
    localSelectedDate && hookSessions.length > 0
      ? hookSessions.find(
          (session) =>
            session.startDateTime.startsWith(toDateString(localSelectedDate)) &&
            session.participantCount === 0 &&
            hasRemainingCapacity(session) &&
            isBookableSessionRow(session),
        )
      : undefined;

  const { capacityLabel, initialPrice, resourceMedia } = useMemo(() => {
    // Price tiers from first available date session (for initial load)
    const firstAvailPriceTiers = firstAvailSession?.priceTiers ?? [];

    // Price tiers from all API sessions (fallback)
    const apiSessionPriceTiers = hookSessions
      .flatMap<PriceTier>((e) => (e ? e.priceTiers ?? [] : []))
      .sort((a, b) => a.price - b.price);

    // Priority: apiSelectedSession > firstAvailSession > all hookSessions
    // eslint-disable-next-line no-nested-ternary
    const priceTiers = apiSelectedSession
      ? apiSelectedSession.priceTiers
      : firstAvailPriceTiers.length > 0
      ? firstAvailPriceTiers
      : apiSessionPriceTiers;

    const startingPrice = priceTiers[0]?.price || 0;
    const displayedCapacity = Number(group.experiences?.[0]?.displayCapacity);

    return {
      resourceMedia: group.medias,
      initialPrice: startingPrice,
      capacityLabel:
        displayedCapacity > 0
          ? `${displayedCapacity} ${
              displayedCapacity === 1
                ? translate('person')
                : translate('people')
            }`
          : '',
    };
  }, [hookSessions, group, translate, apiSelectedSession, firstAvailSession]);

  const useApiData = hookSessions.length > 0 && !isSessionsLoading;
  // eslint-disable-next-line no-nested-ternary
  const sessionStartTime = useApiData
    ? apiSelectedSession?.startDateTime.split('T')[1]
    : firstAvailSession?.startTime
    ? `${firstAvailSession.startTime}:00`
    : undefined;

  const sessionDuration = useApiData
    ? apiSelectedSession?.duration
    : firstAvailSession?.duration;

  const sessionDateStr = useApiData
    ? apiSelectedSession?.startDateTime.split('T')[0]
    : resourceGroupFirstAvailableDate?.scheduledDate;

  // Build session date for display
  const sessionDate =
    sessionDateStr && sessionStartTime
      ? fromDateAndTimeString(sessionDateStr, sessionStartTime)
      : undefined;

  // Calculate end time from duration
  const endSessionDate =
    sessionDate && sessionDuration
      ? new Date(sessionDate.getTime() + sessionDuration * 60 * 1000)
      : undefined;

  const experienceTaxes = group.experiences[0]?.taxes as unknown as ITax[];
  // A flexible or multi-day group's tier rate is per increment or per day, not something a guest can
  // book, so its card advertises the price of its shortest booking instead.
  const continuousPrice = useContinuousStartingPrice({
    date: localSelectedDate ? toDateString(localSelectedDate) : undefined,
    group,
    scheduleType: listing.scheduleType,
    spans: hookSessions,
  });
  const isContinuous = isContinuousScheduleType(listing.scheduleType);
  // eslint-disable-next-line no-nested-ternary
  const fromPrice = continuousPrice
    ? currencyFormat(currency)(formattedNumber(continuousPrice.amount))
    : !isContinuous && initialPrice
    ? currencyFormat(currency)(
        formattedNumber(
          calculateAdvertisedTierPrice(initialPrice, experienceTaxes ?? []),
        ),
      )
    : '';
  const isDayBased = continuousPrice?.isDayBased ?? false;

  const handleIsDayBlocked = (day: moment.Moment) => {
    if (!hookSessions) return true;

    // A continuously booked resource has open spans in place of sessions; a bookable one opens the day.
    return !hookSessions.some(
      (session) =>
        session.startDateTime.startsWith(toDateString(fromMoment(day))) &&
        (session.status === SessionStatus.OPEN ||
          (session.isContinuousAvailability && isBookableSessionRow(session))),
    );
  };

  const handleNewDatePickerDateChange = (date: moment.Moment | null) => {
    trackAnalyticsEvent();
    setLocalSelectedDate(date ? date.toDate() : undefined);
    setHasManuallySelectedLocalDate(true);
  };

  const handleMonthChange = (date: Date) => {
    setDatePickerViewedMonth(getEndOfMonth(date));
  };

  const hasNoFutureAvailability =
    !isLoadingFirstAvailableDate &&
    firstAvailableDate &&
    !resourceGroupFirstAvailableDate;

  const noSessionForSelectedDate =
    !isSessionsLoading &&
    hookSessions.length > 0 &&
    !isViewingDifferentMonth &&
    !apiSelectedSession;

  const isUnavailable = hasNoFutureAvailability || noSessionForSelectedDate;
  const hideCta =
    isLoadingFirstAvailableDate ||
    (!hasManuallySelectedDate && hasNoFutureAvailability);

  return (
    <NonMappedResourceGroupCard
      cancellationPolicyText={
        cancellationPolicy?.active ? cancellationPolicy.notes : undefined
      }
      capacityCount={Number(group.experiences?.[0]?.displayCapacity ?? 0)}
      capacityLabel={capacityLabel}
      // Navigate to the group sub-page by slug when available (falling back to the UUID) so the URL
      // stays keyword-rich; exchange-id (PR1) resolves either form. This prop only builds the route.
      collectionId={listing.slug ?? listing.resourceGroupCollectionId}
      bookingUnitLabel={
        isDayBased
          ? translate(
              group.availabilityRule?.timeFrame ===
                AvailabilityTimeFrame.NIGHTLY
                ? 'bookedByNight'
                : 'bookedByDay',
            )
          : undefined
      }
      daySessionRangeFrom={
        sessionDate && !isDayBased
          ? format(sessionDate, 'p', { locale })
          : undefined
      }
      daySessionRangeTo={
        endSessionDate && !isDayBased
          ? format(endSessionDate, 'p', { locale })
          : undefined
      }
      description={group.description ?? 'Description unavailable'}
      detailHasMoreToShow={false}
      displayDate={localSelectedDate}
      fromPrice={fromPrice}
      fromPriceUnit={continuousPrice?.unitLabel}
      groupId={group.id}
      handleIsDayBlocked={handleIsDayBlocked}
      hideCta={hideCta}
      resourceMedia={resourceMedia}
      isLoading={isLoading}
      newDatePickerOpen={datePickerOpen}
      onMonthChange={handleMonthChange}
      onNewDatePickerDateChange={handleNewDatePickerDateChange}
      onNewDatePickerOpenChange={setDatePickerOpen}
      onReserveClick={trackAnalyticsEvent}
      resourceGroupFullDetailDescription={
        group.description ?? 'Description unavailable'
      }
      resourceGroupFullDetailIncludedItems={group.included}
      sessionDuration={sessionDuration}
      sessionTime={sessionStartTime}
      title={group.title ?? 'Title unavailable'}
      unavailable={isUnavailable}
      vibes={vibes}
      collectionCustomCallToAction={listing.customCallToAction}
      isSessionsLoading={isSessionsLoading || isLoadingFirstAvailableDate}
      promoterBrand={promoterBrand}
      brandFlagName={
        eligibleProgram && Object.values(eligibleProgram)[0]?.payEligible
          ? Object.values(eligibleProgram)[0]?.org
          : undefined
      }
    />
  );
};

export default NonMappedResourceGroupCardWrapper;
